import { track, init, setUserId, identify, Identify } from '@amplitude/analytics-browser';

export interface AmplitudeEvent {
  eventType: string;
  eventProperties?: Record<string, any>;
  userProperties?: Record<string, any>;
}

export interface AmplitudeUser {
  userId?: string;
  userProperties?: Record<string, any>;
}

class AmplitudeService {
  private isInitialized = false;
  private apiKey: string | null = null;

  /**
   * Initialize Amplitude with API key
   */
  init(apiKey: string): void {
    if (this.isInitialized) {
      console.warn('Amplitude is already initialized');
      return;
    }

    this.apiKey = apiKey;

    init(apiKey, {
      // Disable automatic page view tracking since we'll handle it manually
      defaultTracking: {
        pageViews: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
      },
    });

    this.isInitialized = true;
  }

  /**
   * Check if Amplitude is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.apiKey !== null;
  }

  /**
   * Track a custom event
   */
  trackEvent(eventType: string, eventProperties?: Record<string, any>): void {
    if (!this.isReady()) {
      console.warn('Amplitude is not initialized. Cannot track event:', eventType);
      return;
    }

    try {
      track(eventType, eventProperties);
    } catch (error) {
      console.error('Error tracking Amplitude event:', error);
    }
  }

  /**
   * Set user ID for tracking
   */
  setUser(userId: string, userProperties?: Record<string, any>): void {
    if (!this.isReady()) {
      console.warn('Amplitude is not initialized. Cannot set user');
      return;
    }

    try {
      setUserId(userId);

      if (userProperties) {
        const identifyObj = new Identify();
        Object.entries(userProperties).forEach(([key, value]) => {
          identifyObj.set(key, value);
        });
        identify(identifyObj);
      }
    } catch (error) {
      console.error('Error setting Amplitude user:', error);
    }
  }

  /**
   * Identify user with properties
   */
  identifyUser(userId: string, userProperties?: Record<string, any>): void {
    if (!this.isReady()) {
      console.warn('Amplitude is not initialized. Cannot identify user');
      return;
    }

    try {
      const identifyObj = new Identify();

      if (userProperties) {
        Object.entries(userProperties).forEach(([key, value]) => {
          identifyObj.set(key, value);
        });
      }

      identify(identifyObj);
    } catch (error) {
      console.error('Error identifying Amplitude user:', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName?: string, pageProperties?: Record<string, any>): void {
    const eventProperties = {
      page_name: pageName || document.title,
      page_url: window.location.href,
      page_path: window.location.pathname,
      ...pageProperties,
    };

    this.trackEvent('Page Viewed', eventProperties);
  }

  /**
   * Track user action
   */
  trackUserAction(action: string, properties?: Record<string, any>): void {
    this.trackEvent(`User Action: ${action}`, properties);
  }

  /**
   * Track asset interaction
   */
  trackAssetInteraction(action: string, assetId: string, properties?: Record<string, any>): void {
    this.trackEvent(`Asset ${action}`, {
      asset_id: assetId,
      ...properties,
    });
  }

  /**
   * Track collection interaction
   */
  trackCollectionInteraction(action: string, collectionId: string, properties?: Record<string, any>): void {
    this.trackEvent(`Collection ${action}`, {
      collection_id: collectionId,
      ...properties,
    });
  }

  /**
   * Track brandfolder interaction
   */
  trackBrandfolderInteraction(action: string, brandfolderId: string, properties?: Record<string, any>): void {
    this.trackEvent(`Brandfolder ${action}`, {
      brandfolder_id: brandfolderId,
      ...properties,
    });
  }

  /**
   * Track search event
   */
  trackSearch(query: string, resultsCount?: number, properties?: Record<string, any>): void {
    this.trackEvent('Search Performed', {
      search_query: query,
      results_count: resultsCount,
      ...properties,
    });
  }

  /**
   * Track download event
   */
  trackDownload(assetId: string, assetType: string, properties?: Record<string, any>): void {
    this.trackEvent('Asset Downloaded', {
      asset_id: assetId,
      asset_type: assetType,
      ...properties,
    });
  }

  /**
   * Track upload event
   */
  trackUpload(assetType: string, fileSize?: number, properties?: Record<string, any>): void {
    this.trackEvent('Asset Uploaded', {
      asset_type: assetType,
      file_size: fileSize,
      ...properties,
    });
  }
}

// Export singleton instance
export const amplitudeService = new AmplitudeService();

// Export the class for testing
export { AmplitudeService };
